import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import { z } from 'zod'
import {
  assistantBehaviorResponseSchema,
  assistantBehaviorUpdateSchema,
  voiceGreetingUpdateSchema,
  textGreetingResponseSchema,
  textGreetingUpdateSchema,
  voiceAIControlSettingsResponseSchema,
  voiceAIControlSettingsUpdateSchema,
  serviceCatalogResponseSchema,
  serviceCatalogUpdateSchema,
  specialRulesResponseSchema,
  voiceRulesReconciliationResponseSchema,
  voiceServiceCatalogReconciliationResponseSchema,
  voiceServiceCatalogStatusSchema,
  type VoiceServiceCatalogStatus,
  type AssistantBehaviorResponse,
  type AssistantBehaviorUpdate,
  type VoiceGreetingUpdate,
  type TextGreetingResponse,
  type TextGreetingUpdate,
  type VoiceAIControlSettingsResponse,
  type VoiceAIControlSettingsUpdate,
  type ServiceCatalogResponse,
  type ServiceCatalogUpdate,
  type SpecialRulesResponse,
} from './assistant-settings-contracts'

export type AssistantSettingsRepository = {
  getBehavior: (tenantId: string) => Promise<AssistantBehaviorResponse>
  updateBehavior: (
    tenantId: string,
    sourceTenantId: string,
    update: AssistantBehaviorUpdate,
    options?: { scope: 'VOICE' }
  ) => Promise<AssistantBehaviorResponse>
  updateVoiceGreeting: (
    tenantId: string,
    sourceTenantId: string,
    update: VoiceGreetingUpdate
  ) => Promise<AssistantBehaviorResponse>
  getTextGreeting: (tenantId: string) => Promise<TextGreetingResponse>
  updateTextGreeting: (
    tenantId: string,
    update: TextGreetingUpdate
  ) => Promise<TextGreetingResponse>
  getVoiceAIControl: (
    tenantId: string
  ) => Promise<VoiceAIControlSettingsResponse>
  updateVoiceAIControl: (
    tenantId: string,
    update: VoiceAIControlSettingsUpdate
  ) => Promise<VoiceAIControlSettingsResponse>
  getRules: (tenantId: string) => Promise<SpecialRulesResponse>
  createRule: (
    tenantId: string,
    revision: number,
    content: string,
    scope: 'GENERAL' | 'VOICE_ONLY'
  ) => Promise<SpecialRulesResponse>
  updateRule: (
    tenantId: string,
    ruleId: string,
    revision: number,
    content: string
  ) => Promise<SpecialRulesResponse>
  setRuleStatus: (
    tenantId: string,
    ruleId: string,
    revision: number,
    status: 'ACTIVE' | 'INACTIVE'
  ) => Promise<SpecialRulesResponse>
  archiveRule: (
    tenantId: string,
    ruleId: string,
    revision: number
  ) => Promise<SpecialRulesResponse>
  reconcileVoiceRules: (
    tenantId: string,
    revision: number,
    fingerprint: string
  ) => Promise<void>
  getServiceCatalog: (tenantId: string) => Promise<ServiceCatalogResponse>
  // Whether the clinic's Voice assistant serves the saved catalog yet.
  getVoiceServiceCatalogStatus: (
    tenantId: string
  ) => Promise<VoiceServiceCatalogStatus>
  // Materializes the saved catalog in the clinic's Voice assistant.
  reconcileVoiceServiceCatalog: (
    tenantId: string,
    catalogFingerprint: string
  ) => Promise<void>
  updateServiceCatalog: (
    tenantId: string,
    update: ServiceCatalogUpdate
  ) => Promise<ServiceCatalogResponse>
}

function commandId() {
  return crypto.randomUUID()
}

const assistantBehaviorErrorSchema = z
  .object({ error: z.string() })
  .transform(({ error }) => ({ message: error }))
// user-management answers rule refusals as { message } (limit, authority text).
const specialRulesErrorSchema = z.object({ message: z.string() })

export function createAssistantSettingsRepository({
  getUser,
  onboardingBaseUrl = getEnvironment().onboardingApiBaseUrl,
  userManagementBaseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  onboardingBaseUrl?: string
  userManagementBaseUrl?: string
  fetchImplementation?: typeof fetch
}): AssistantSettingsRepository {
  const onboarding = createApiClient({
    baseUrl: onboardingBaseUrl,
    getUser,
    fetchImplementation,
  })
  const userManagement = createApiClient({
    baseUrl: userManagementBaseUrl,
    getUser,
    fetchImplementation,
  })
  const rulesPath = (tenantId: string) =>
    `/dashboard/clinics/${encodeURIComponent(tenantId)}/special-rules`
  const serviceCatalogPath = (tenantId: string) =>
    `/dashboard/clinics/${encodeURIComponent(tenantId)}/service-catalog`

  return {
    getBehavior(tenantId) {
      return onboarding.request('/v1/settings/assistant-behavior', {
        method: 'POST',
        body: { tenantUid: tenantId },
        responseSchema: assistantBehaviorResponseSchema,
      })
    },
    updateBehavior(tenantId, sourceTenantId, update, options) {
      const parsed = assistantBehaviorUpdateSchema.parse({
        ...update,
        behavior: {
          ...update.behavior,
          aiName: update.behavior.aiName.trim(),
          firstMessage: update.behavior.firstMessage.trim(),
          endCallMessage: update.behavior.endCallMessage.trim(),
        },
      })
      return onboarding.request('/v1/settings/assistant-behavior', {
        method: 'PUT',
        body: {
          tenantUid: tenantId,
          sourceTenantUid: sourceTenantId,
          ...parsed,
          ...(options ? { updateScope: options.scope } : {}),
        },
        responseSchema: assistantBehaviorResponseSchema,
        errorSchema: assistantBehaviorErrorSchema,
      })
    },
    updateVoiceGreeting(tenantId, sourceTenantId, update) {
      const parsed = voiceGreetingUpdateSchema.parse(update)
      return onboarding.request('/v1/settings/assistant-behavior', {
        method: 'PUT',
        body: {
          tenantUid: tenantId,
          sourceTenantUid: sourceTenantId,
          ...parsed,
          updateScope: 'VOICE_GREETING',
        },
        responseSchema: assistantBehaviorResponseSchema,
        errorSchema: assistantBehaviorErrorSchema,
      })
    },
    getTextGreeting(tenantId) {
      return onboarding.request('/v1/settings/text-greeting', {
        method: 'POST',
        body: { tenantUid: tenantId },
        responseSchema: textGreetingResponseSchema,
      })
    },
    updateTextGreeting(tenantId, update) {
      const parsed = textGreetingUpdateSchema.parse(update)
      return onboarding.request('/v1/settings/text-greeting', {
        method: 'PUT',
        body: { tenantUid: tenantId, ...parsed },
        responseSchema: textGreetingResponseSchema,
      })
    },
    getVoiceAIControl(tenantId) {
      return onboarding.request('/v1/settings/voice-ai-control', {
        method: 'POST',
        body: { tenantUid: tenantId },
        responseSchema: voiceAIControlSettingsResponseSchema,
      })
    },
    updateVoiceAIControl(tenantId, update) {
      const parsed = voiceAIControlSettingsUpdateSchema.parse(update)
      return onboarding.request('/v1/settings/voice-ai-control', {
        method: 'PUT',
        body: { tenantUid: tenantId, ...parsed },
        responseSchema: voiceAIControlSettingsResponseSchema,
      })
    },
    getRules(tenantId) {
      return userManagement.request(rulesPath(tenantId), {
        method: 'GET',
        responseSchema: specialRulesResponseSchema,
      })
    },
    createRule(tenantId, revision, content, scope) {
      return userManagement.request(rulesPath(tenantId), {
        method: 'POST',
        body: {
          expectedCollectionRevision: revision,
          commandId: commandId(),
          content,
          scope,
        },
        responseSchema: specialRulesResponseSchema,
        errorSchema: specialRulesErrorSchema,
      })
    },
    updateRule(tenantId, ruleId, revision, content) {
      return userManagement.request(
        `${rulesPath(tenantId)}/${encodeURIComponent(ruleId)}`,
        {
          method: 'PUT',
          body: {
            expectedRevision: revision,
            commandId: commandId(),
            content,
          },
          responseSchema: specialRulesResponseSchema,
          errorSchema: specialRulesErrorSchema,
        }
      )
    },
    setRuleStatus(tenantId, ruleId, revision, status) {
      return userManagement.request(
        `${rulesPath(tenantId)}/${encodeURIComponent(ruleId)}/status`,
        {
          method: 'POST',
          body: { expectedRevision: revision, commandId: commandId(), status },
          responseSchema: specialRulesResponseSchema,
          errorSchema: specialRulesErrorSchema,
        }
      )
    },
    archiveRule(tenantId, ruleId, revision) {
      return userManagement.request(
        `${rulesPath(tenantId)}/${encodeURIComponent(ruleId)}`,
        {
          method: 'DELETE',
          body: { expectedRevision: revision, commandId: commandId() },
          responseSchema: specialRulesResponseSchema,
          errorSchema: specialRulesErrorSchema,
        }
      )
    },
    async reconcileVoiceRules(tenantId, revision, fingerprint) {
      await onboarding.request('/v1/settings/voice-rules/reconciliation', {
        method: 'POST',
        body: {
          tenantUid: tenantId,
          expectedCollectionRevision: revision,
          expectedCollectionFingerprint: fingerprint,
        },
        responseSchema: voiceRulesReconciliationResponseSchema,
      })
    },
    getVoiceServiceCatalogStatus(tenantId) {
      return onboarding.request('/v1/settings/voice-service-catalog/status', {
        method: 'POST',
        body: { tenantUid: tenantId },
        responseSchema: voiceServiceCatalogStatusSchema,
      })
    },
    async reconcileVoiceServiceCatalog(tenantId, catalogFingerprint) {
      await onboarding.request(
        '/v1/settings/voice-service-catalog/reconciliation',
        {
          method: 'POST',
          body: {
            tenantUid: tenantId,
            expectedCatalogFingerprint: catalogFingerprint,
          },
          responseSchema: voiceServiceCatalogReconciliationResponseSchema,
        }
      )
    },
    getServiceCatalog(tenantId) {
      return userManagement.request(serviceCatalogPath(tenantId), {
        method: 'GET',
        responseSchema: serviceCatalogResponseSchema,
      })
    },
    updateServiceCatalog(tenantId, update) {
      const parsed = serviceCatalogUpdateSchema.parse({
        ...update,
        services: update.services.map((service) => ({
          ...service,
          name: service.name.trim(),
        })),
      })
      return userManagement.request(serviceCatalogPath(tenantId), {
        method: 'PUT',
        body: parsed,
        responseSchema: serviceCatalogResponseSchema,
      })
    },
  }
}
